import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { Dumbbell, Loader2, ArrowRight } from 'lucide-react';

export function ReferralPage() {
    const { slug } = useParams<{ slug: string }>();

    const { data: codeData, isLoading, error } = useQuery({
        queryKey: ['affiliate-code', slug],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('affiliate_codes')
                .select(`
          id,
          code,
          link_slug,
          discount_percent,
          status,
          influencer:influencer_id (
            name
          )
        `)
                .eq('link_slug', slug)
                .eq('status', 'active')
                .single();

            if (error) throw error;
            return data;
        },
        enabled: !!slug,
    });

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-600 to-primary-800">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
            </div>
        );
    }

    if (error || !codeData) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-600 to-primary-800 p-6">
                <div className="text-center text-white">
                    <h1 className="text-3xl font-bold mb-4">Link inválido</h1>
                    <p className="text-primary-100 mb-6">
                        Este link de indicação não existe ou foi desativado.
                    </p>
                    <Link to="/signup" className="btn bg-white text-primary-700 hover:bg-primary-50">
                        Criar conta
                    </Link>
                </div>
            </div>
        );
    }

    const influencerData = codeData.influencer as { name: string } | { name: string }[] | null;
    const influencer = Array.isArray(influencerData) ? influencerData[0] : influencerData;

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-600 via-primary-700 to-accent-600 flex items-center justify-center p-6">
            <div className="max-w-md w-full">
                {/* Logo */}
                <div className="text-center mb-8">
                    <img src="/gofit-logo.png" alt="GoFit" className="w-16 h-16 mx-auto mb-4" />
                    <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        GoFit
                    </h1>
                </div>

                {/* Card */}
                <div className="bg-white rounded-2xl shadow-xl p-8">
                    <div className="text-center mb-6">
                        <div className="inline-flex items-center gap-2 bg-primary-50 text-primary-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                            🎉 Indicação de {influencer?.name || 'Parceiro'}
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            Você ganhou {codeData.discount_percent}% de desconto!
                        </h2>
                        <p className="text-gray-600">
                            Crie sua conta agora e aproveite o desconto em qualquer plano premium.
                        </p>
                    </div>

                    {/* Discount badge */}
                    <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl p-6 text-white text-center mb-6">
                        <p className="text-sm opacity-90 mb-1">Seu código de desconto</p>
                        <p className="text-3xl font-bold tracking-wider">{codeData.code}</p>
                        <p className="text-sm opacity-90 mt-1">
                            {codeData.discount_percent}% OFF em todos os planos
                        </p>
                    </div>

                    {/* CTAs */}
                    <div className="space-y-3">
                        <Link
                            to={`/signup?ref=${codeData.code}`}
                            className="btn-primary w-full justify-center"
                        >
                            Criar conta com desconto
                            <ArrowRight className="w-4 h-4 ml-2" />
                        </Link>
                        <Link
                            to="/login"
                            className="btn-ghost w-full justify-center text-gray-600"
                        >
                            Já tenho uma conta
                        </Link>
                    </div>
                </div>

                {/* Footer */}
                <p className="text-center text-primary-100 text-sm mt-6">
                    O desconto será aplicado automaticamente na sua assinatura
                </p>
            </div>
        </div>
    );
}
